import { FC, KeyboardEvent, useEffect, useRef, MutableRefObject } from 'react';
import classNames from 'classnames/bind';
import { Theme } from '../../types/enums';
import { FAIcon } from '../Icons/Icons';
import GenericLink from '../GenericLink/GenericLink';
import { filterIconTheme } from '../utils/ThemeFilter/ThemeFilter';
import { RawLinkPropsTypes } from './types/propsType';

import { TextLinkUnderlineStyle } from './types/enums';
import styles from './TextLink.module.scss';

const cx = classNames.bind(styles);
/**
 *
 * @description RawTextLink provide <a></a> with basic css;
 *  it can be used only through other Components.
 *  styling can be override through prop (className) only.
 *  Ex-  TextLink, Card with Images.
 *  Note :-  TextLink uses GenericLink.
 */

const RawTextLink: FC<RawLinkPropsTypes> = ({
  text,
  className,
  disabled,
  href = '',
  theme = Theme.BLACK,
  leftIcon,
  rightIcon,
  underline = TextLinkUnderlineStyle.HOVER,
  onClick,
  forwardedRef,
  ariaLabel,
  iconPosition, // extract away from rest props to avoid getting attached to raw DOM element
  ...restProps
}) => {
  const anchorRef = useRef() as MutableRefObject<HTMLSpanElement>;
  //filters underline class corresponding to prop
  const { NOHOVER, FIXED } = TextLinkUnderlineStyle;
  const CClassname = cx(
    'text-link',
    {
      'no-hover-underline': underline === NOHOVER,
      'fixed-underline': underline === FIXED,
    },
    [theme],
    className,
    { disabled: disabled },
  );
  const onClickWrapper: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick(e);
    }
  };
  useEffect(() => {
    if (onClick && anchorRef.current) {
      const anchorParentElement = anchorRef.current;
      const anchorElement = anchorParentElement.getElementsByTagName('a');
      anchorElement[0].addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
      });
    }
  }, [onClick, anchorRef.current]);
  return (
    <span
      role="link"
      aria-label={ariaLabel}
      className={CClassname}
      data-testid="textlink-test-id"
      onClick={onClickWrapper}
      onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => {
        if (e.key === 'Enter') {
          restProps.onKeyDown?.(e);
        }
      }}
      tabIndex={0}
      ref={anchorRef}
    >
      {leftIcon && (
        <FAIcon
          {...leftIcon}
          theme={filterIconTheme(theme)}
          className={styles['icon']}
          data-testid="textlink-left-icon-testid"
        />
      )}
      <GenericLink href={href} {...restProps} forwardedRef={forwardedRef}>
        {text}
      </GenericLink>
      {rightIcon && (
        <FAIcon
          {...rightIcon}
          theme={filterIconTheme(theme)}
          className={styles['icon']}
          data-testid="textlink-right-icon-testid"
        />
      )}
    </span>
  );
};
RawTextLink.displayName = 'RawTextLink';
export default RawTextLink;
